import React, { useState, useEffect, useCallback } from "react";
import "./JunkMail.css";
import { ArrowLeft, Download, User } from "lucide-react";
import { Spinner, Modal, Button } from "react-bootstrap";
import { apiCall } from "../../services/api-service";

import excelIcon from "../../Assets/png/excel.png";
import docsIcon from "../../Assets/png/docs.png";
import fileIcon from "../../Assets/png/file.png";
import imageIcon from "../../Assets/png/image.png";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) + " at " + date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).replace(" ", "");
  } catch {
    return dateStr;
  }
};

const getInitials = (name, email) => {
  const str = name || email || "JM";
  const parts = str.split(/[\s._@-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return str.substring(0, 2).toUpperCase();
};

const renderStatusBadge = (statusStr) => {
  const status = (statusStr || "").toLowerCase();
  if (status.includes("user") || status.includes("create_user")) {
    return <span className="status-pill pill-user-ticket">User & Ticket Created</span>;
  } else if (status.includes("ticket") || status.includes("create_ticket") || status.includes("approved") || status.includes("approve")) {
    return <span className="status-pill pill-ticket-created">Ticket Created</span>;
  } else {
    return <span className="status-pill pill-rejected">Rejected</span>;
  }
};

const formatFileSize = (sizeVal) => {
  if (sizeVal === null || sizeVal === undefined || sizeVal === "") {
    return "-";
  }

  if (typeof sizeVal === "string" && /[a-zA-Z]/.test(sizeVal)) {
    return sizeVal;
  }

  const kb = Number(sizeVal);
  if (isNaN(kb) || kb <= 0) return "0 Bytes";

  if (kb < 1) {
    return `${Math.round(kb * 1024)} Bytes`;
  } else if (kb < 1024) {
    return `${parseFloat(kb.toFixed(1))} KB`;
  } else if (kb < 1024 * 1024) {
    return `${parseFloat((kb / 1024).toFixed(1))} MB`;
  } else {
    return `${parseFloat((kb / (1024 * 1024)).toFixed(1))} GB`;
  }
};

const isImageFile = (ext) => ["JPG", "JPEG", "PNG", "GIF", "WEBP", "SVG"].includes(ext);
const isExcelFile = (ext) => ["XLS", "XLSX", "CSV"].includes(ext);
const isWordFile = (ext) => ["DOC", "DOCX"].includes(ext);
const isPdfFile = (ext) => ext === "PDF";

const renderAttachmentIcon = (fileName, fileUrl) => {
  const ext = (fileName.split(".").pop() || "").toUpperCase();

  if (isImageFile(ext)) {
    return (
      <img
        src={fileUrl}
        alt={fileName}
        className="attachment-img-preview"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = imageIcon;
        }}
      />
    );
  } else if (isExcelFile(ext)) {
    return <img src={excelIcon} alt="Excel" className="attachment-icon-img" />;
  } else if (isWordFile(ext)) {
    return <img src={docsIcon} alt="Word" className="attachment-icon-img" />;
  } else if (isPdfFile(ext)) {
    return (
      <div className="attachment-type-badge">
        PDF
      </div>
    );
  } else {
    return <img src={fileIcon} alt="File" className="attachment-icon-img" />;
  }
};

const JunkMailView = ({ selectedMailId, onBack, activeTab, onActionSuccess, triggerToast }) => {
  const [viewMailData, setViewMailData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [activeActionType, setActiveActionType] = useState(null);

  // Action Comments Modal State
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [actionReason, setActionReason] = useState("");

  const fetchMailDetails = useCallback(async () => {
    if (!selectedMailId) return;
    setViewLoading(true);
    setViewMailData(null);
    try {
      const response = await apiCall("get", `junk_mail/view/${selectedMailId}`);
      if (response?.status === 200 && response?.data?.success) {
        const mailData = response.data.data || {};
        try {
          const attResponse = await apiCall("get", `junk_mail/attachments/${selectedMailId}`);
          if (attResponse?.status === 200 && attResponse?.data?.success && Array.isArray(attResponse.data.attachments)) {
            mailData.attachments = attResponse.data.attachments;
          }
        } catch (attErr) {
          console.warn("Could not fetch separate attachments:", attErr);
        }
        setViewMailData(mailData);
      } else {
        setViewMailData(null);
      }
    } catch (error) {
      console.error("Error viewing junk mail:", error);
      setViewMailData(null);
    } finally {
      setViewLoading(false);
    }
  }, [selectedMailId]);

  useEffect(() => {
    fetchMailDetails();
  }, [fetchMailDetails]);

  const triggerActionModal = (action) => {
    setActionTarget({ action });
    setActionReason("");
    setShowActionModal(true);
  };

  const handleConfirmAction = () => {
    if (!actionTarget) return;
    handleAction(actionTarget.action, actionReason);
  };

  const handleAction = async (action, reason = "") => {
    if (!selectedMailId) return;
    setActiveActionType(action);
    try {
      const response = await apiCall("post", "junk_mail/action", {
        id: selectedMailId,
        action: action,
        reason: reason,
      });

      if (response?.status === 200 && response?.data?.success) {
        triggerToast(response.data.message || "Action completed successfully!", true);
        if (onActionSuccess) onActionSuccess();
        if (onBack) onBack();
      } else {
        const errorMsg =
          response?.data?.message ||
          response?.data?.error ||
          "Action failed. Please try again.";
        triggerToast(errorMsg, false);
      }
    } catch (error) {
      console.error("Error performing action on junk mail:", error);
      triggerToast("Error processing action.", false);
    } finally {
      setActiveActionType(null);
      setShowActionModal(false);
    }
  };

  const handleDownloadAttachment = (e, fileUrl, fileName) => {
    if (!fileUrl || fileUrl === "#") {
      e.preventDefault();
      return;
    }

    if (fileUrl.startsWith("data:") || fileUrl.startsWith("blob:")) {
      e.preventDefault();
      const a = document.createElement("a");
      a.href = fileUrl;
      a.download = fileName || "attachment";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="junk-mail-container">
      <div className="junk-view-container">
        {/* Header Section */}
        <div className="junk-view-header">
          <div className="junk-view-left-group">
            <button
              className="back-circle-btn"
              onClick={onBack}
              title="Back to list"
            >
              <ArrowLeft size={18} />
            </button>

            <div className="junk-view-title-info">
              <h2 className="junk-view-subject">
                {viewLoading ? "Loading details..." : (viewMailData?.subject || "No Subject")}
              </h2>

              {!viewLoading && viewMailData && (
                <div className="junk-view-sender-bar">
                  <div className="sender-avatar-circle">
                    {getInitials(viewMailData.from_name, viewMailData.from_email)}
                  </div>
                  <div className="sender-info-text">
                    <div className="sender-name-email">
                      {viewMailData.from_name || "Sender"}{" "}
                      <span className="sender-email-text">
                        &lt;{viewMailData.from_email || "-"}&gt;
                      </span>
                    </div>
                    <div className="sender-date-text">
                      {formatDateTime(viewMailData.email_time || viewMailData.created_at)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons Top Right */}
          {!viewLoading && viewMailData && (
            <div className="junk-view-actions-right">
              {activeTab === "history" || (viewMailData.status && viewMailData.status.toLowerCase() !== "pending") ? (
                renderStatusBadge(viewMailData.status || "Rejected")
              ) : (
                <>
                  <button
                    className="btn-bulk-reject"
                    disabled={!!activeActionType}
                    onClick={() => triggerActionModal("Reject")}
                  >
                    {activeActionType === "Reject" ? <Spinner animation="border" size="sm" /> : "Reject"}
                  </button>
                  <button
                    className="btn-bulk-create-ticket"
                    disabled={!!activeActionType}
                    onClick={() => triggerActionModal("Create_Ticket")}
                  >
                    {activeActionType === "Create_Ticket" ? <Spinner animation="border" size="sm" /> : "Create Ticket"}
                  </button>
                  <button
                    className="btn-bulk-create-user-ticket"
                    disabled={!!activeActionType}
                    onClick={() => triggerActionModal("Create_User_and_Ticket")}
                  >
                    {activeActionType === "Create_User_and_Ticket" ? <Spinner animation="border" size="sm" /> : "Create user & Ticket"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Mail Content Card */}
        <div className="junk-view-body-card">
          {viewLoading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
              <Spinner animation="border" style={{ color: "#6929E2" }} />
            </div>
          ) : viewMailData ? (
            <>
              <div className="junk-mail-html-render">
                {viewMailData.content ? (
                  <div dangerouslySetInnerHTML={{ __html: viewMailData.content }} />
                ) : (
                  <div style={{ color: "#98A2B3", fontStyle: "italic" }}>
                    No content body available for this mail.
                  </div>
                )}
              </div>

              {/* Attachments Section */}
              {Array.isArray(viewMailData.attachments) && viewMailData.attachments.length > 0 && (
                <div className="junk-attachments-wrapper">
                  <div className="junk-attachments-heading">ATTACHMENTS ({viewMailData.attachments.length})</div>
                  <div className="junk-attachments-list">
                    {viewMailData.attachments.map((att, idx) => {
                      const fileUrl = typeof att === "string" ? att : (att.url || (att.base64 ? `data:application/octet-stream;base64,${att.base64}` : (att.path || "#")));
                      const rawName = typeof att === "string" ? att.split("/").pop() : (att.name || att.filename || `Attachment_${idx + 1}`);
                      const fileName = rawName.includes("_") && rawName.split("_").length > 1 && !isNaN(rawName.split("_")[0]) ? rawName.split("_").slice(1).join("_") : rawName;
                      const rawSize = typeof att === "object" ? (att.size ?? att.file_size ?? att.filesize) : null;
                      const fileSize = formatFileSize(rawSize);

                      return (
                        <a
                          href={fileUrl}
                          target="_self"
                          download={fileName}
                          onClick={(e) => handleDownloadAttachment(e, fileUrl, fileName)}
                          className="attachment-file-card"
                          key={idx}
                          style={{ textDecoration: "none" }}
                        >
                          {renderAttachmentIcon(fileName, fileUrl)}
                          <div className="attachment-details">
                            <span className="attachment-file-name" title={fileName}>{fileName}</span>
                            <span className="attachment-file-size">{fileSize}</span>
                          </div>
                          <span
                            className="attachment-dl-btn"
                            title={`Download ${fileName}`}
                          >
                            <Download size={16} />
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", color: "#667085", padding: "40px" }}>
              Failed to load mail content.
            </div>
          )}
        </div>
      </div>

      {/* Action Comments / Remarks Modal */}
      <Modal
        show={showActionModal}
        onHide={() => !activeActionType && setShowActionModal(false)}
        centered
      >
        <Modal.Header closeButton={!activeActionType}>
          <Modal.Title style={{ fontSize: "16px", fontWeight: "700", color: "#101828" }}>
            {actionTarget?.action === "Reject"
              ? "Reject Junk Mail Request"
              : actionTarget?.action === "Create_Ticket"
              ? "Create Ticket"
              : "Create User & Ticket"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "12px", fontSize: "14px", color: "#475467" }}>
            {actionTarget?.action === "Reject"
              ? "Please enter comments / reason for rejection:"
              : "Add any comments / remarks for this action (optional):"}
          </div>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Type comments / reason here..."
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            style={{ borderRadius: "8px", borderColor: "#D0D5DD", fontSize: "14px" }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={!!activeActionType}
            onClick={() => setShowActionModal(false)}
            style={{ borderRadius: "20px", fontWeight: "600" }}
          >
            Cancel
          </Button>
          <Button
            style={{
              backgroundColor:
                actionTarget?.action === "Reject"
                  ? "#FF4D4F"
                  : actionTarget?.action === "Create_Ticket"
                  ? "#2CC879"
                  : "#6929E2",
              borderColor: "transparent",
              borderRadius: "20px",
              fontWeight: "600",
            }}
            disabled={!!activeActionType}
            onClick={handleConfirmAction}
          >
            {activeActionType ? (
              <Spinner animation="border" size="sm" />
            ) : actionTarget?.action === "Reject" ? (
              "Confirm Reject"
            ) : (
              "Confirm Action"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JunkMailView;
